import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Camera, CameraOff, Settings, Eye } from 'lucide-react';
import Table from '@/components/Table';
import DefaultColumn from '@/components/Table/components/DefaultColumn';
import TableRowActions from '@/components/Table/components/TableRowActions';
import { TableData } from '@/components/Table/type';
import { useTable } from '@/hooks/useTable';
import { MarketResearch } from '@/types/MarketResearch';
import { formatDateToString } from '@/utils/date';
import { useBaseStore } from '@/store';
import { Store as StoreZ } from '@/store/type';

export type MarketResearchTable = TableData<MarketResearch>;

interface TableResearchProps {
  data: MarketResearch[];
  isFetching?: boolean;
  isLoading?: boolean;
}

const columnHelper = createColumnHelper<MarketResearchTable>();

const statusCard = (status: string) => {
  switch (status) {
    case 'Rascunho':
      return 'status-sketch';
    case 'Publicado':
      return 'status-published';
    case 'Encerrado':
      return 'status-closed';
    default:
      return '';
  }
};

const stateSelector = (state: StoreZ) => ({
  profile: state.authSlice.state.profile,
});

const TableResearch = ({ data, isFetching, isLoading }: TableResearchProps) => {
  const navigate = useNavigate();
  const { profile } = useBaseStore(stateSelector);

  const { setTable, tableState } = useTable<MarketResearchTable>('');

  const memoData = useMemo(() => (data || []) as MarketResearchTable[], [data]);

  const memoColumns = useMemo(
    () => [
      columnHelper.accessor('NM_PESQUISA', {
        id: 'NM_PESQUISA',
        header: 'Pesquisa',
        size: 220,
        cell: ({ getValue }) => (
          <span className="font-semibold text-gray-900">{getValue()}</span>
        ),
      }),
      columnHelper.accessor('DD_STATUS', {
        id: 'DD_STATUS',
        header: 'Status',
        size: 130,
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <span className={`status-badge status ${statusCard(status)}`}>
              {status}
            </span>
          );
        },
      }),
      columnHelper.accessor('DD_ORIGEM', {
        id: 'DD_ORIGEM',
        header: 'Origem',
        size: 110,
        cell: ({ getValue }) => {
          const origem = getValue();
          const isRevenda = origem === 'REVENDA';
          return (
            <span
              className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                isRevenda
                  ? 'bg-purple-100 text-purple-800 border border-purple-200'
                  : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}
            >
              {isRevenda ? 'Revenda' : 'Central'}
            </span>
          );
        },
      }),
      columnHelper.accessor('DS_REVENDA', {
        id: 'DS_REVENDA',
        header: 'Revenda',
        size: 200,
        cell: ({ getValue }) => getValue() || '-',
      }),
      columnHelper.accessor('DD_TIPO_CICLO', {
        id: 'DD_TIPO_CICLO',
        header: 'Tipo Ciclo',
        size: 130,
        cell: ({ getValue }) => (
          <span className="text-xs font-medium text-gray-700">
            {getValue()}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'PERIODO',
        header: 'Período',
        size: 190,
        cell: ({ row }) => {
          const inicio = row.original.SK_TEMPO_INICIO;
          const fim = row.original.SK_TEMPO_FIM;
          if (!inicio || !fim) return '-';
          return `${formatDateToString(inicio)} - ${formatDateToString(fim)}`;
        },
      }),
      columnHelper.accessor('DD_COLETA_IMAGEM', {
        id: 'DD_COLETA_IMAGEM',
        header: 'Coleta Imagem',
        size: 120,
        cell: ({ getValue }) =>
          getValue() ? (
            <Camera size={18} className="text-gray-700" />
          ) : (
            <CameraOff size={18} className="text-gray-400" />
          ),
      }),
      columnHelper.display({
        id: 'Ações',
        header: 'Ações',
        size: 90,
        cell: ({ row }) => {
          const research = row.original;
          const canConfigure = [
            'ADMINISTRADOR',
            'ADMINISTRADOR_REVENDA',
          ].includes(profile!);
          const canViewDetails = ['Publicado', 'Encerrado'].includes(
            research.DD_STATUS,
          );

          const actions: any[] = [];

          if (canConfigure) {
            actions.push({
              label: 'Configurações da Pesquisa',
              icon: <Settings size={14} />,
              action: () => navigate(`settings?search=${research.SK_PESQUISA}`),
            });
          }

          if (canViewDetails) {
            actions.push({
              label: 'Ver detalhes da pesquisa',
              icon: <Eye size={14} />,
              action: () => navigate(`details?search=${research.SK_PESQUISA}`),
            });
          }

          if (actions.length === 0) return null;

          return <TableRowActions actions={actions} />;
        },
      }),
    ],
    [profile, navigate],
  );

  return (
    <div className="w-full h-[550px] flex flex-col overflow-hidden">
      <Table<MarketResearchTable>
        data={memoData}
        columns={memoColumns}
        getRowId={(row) => row.SK_PESQUISA.toString()}
        getTableInstance={(instance) => setTable(instance)}
        tableState={tableState}
        isFetching={isFetching}
        isLoading={isLoading}
        shouldRenderCheckbox={false}
        defaultColumn={{
          cell: ({ getValue }) => (
            <DefaultColumn>{getValue() as string}</DefaultColumn>
          ),
        }}
      />
    </div>
  );
};

export default TableResearch;
